import {
  PriorAuthGateOutcomeT,
  type FormularyResponseT,
  type PatientSpecificPricingCoverageT,
  type PayerAlternativeT,
  type PayerCopayT,
  type PayerInformationT,
  type PrescriptionBenefitPayerAlternativeDetailT,
  type PrescriptionBenefitsPharmacyT,
  type PrescriptionBenefitsResponseT,
  type PricingCoverageFactorsT,
  type TherapeuticAlternativesT,
} from '@lotushealth/lotushealth_js_sdk/schemas';
import {
  ArrowRight,
  ChevronDown,
  DollarSign,
  Loader2,
  Pill,
  ShieldCheck,
  Store,
} from 'lucide-react';
import { useState } from 'react';

import type { RxMedication } from '@/entities/rx';
import { Button } from '@/shared/ui/shadcn/button';
import { Checkbox } from '@/shared/ui/shadcn/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { cn } from '@/lib/utils';

import { FormularyDetailDialog } from './FormularyDetailDialog';

import {
  type FormularyDisplayState,
  bucketFormularyStatus,
  describeCopay,
  describeFormularyCoverage,
  deriveFormularyDisplayState,
  formatDollar,
  formatPercent,
  formularyStatusLabel,
  pharmacyTypeLabel,
  pickAlternativeRetailCopay,
} from '../model/formularyDisplayPolicy';

type AlternativeSelectionHandler = (
  dispensableDrugId: number
) => void | boolean | Promise<void | boolean>;

export interface BenefitsCoordinationPanelProps {
  payerInformation: PayerInformationT | null | undefined;
  formulary: FormularyResponseT | null | undefined;
  benefits: PrescriptionBenefitsResponseT | null | undefined;
  /** The currently-selected medication, used for multi-ingredient + obsolete policy. */
  medication: RxMedication | null | undefined;
  /** True once the caller knows a coverage lookup completed, even if it returned no payloads. */
  hasCoverageResponse?: boolean;
  /** When true, shows that the patient cost estimate is being refreshed. */
  isLoadingBenefits?: boolean;
  /** Preview outcome used to explain the next insurance action in clinician language. */
  priorAuthOutcome?: PriorAuthGateOutcomeT | null;
  expedited?: boolean;
  onExpeditedChange?: (checked: boolean) => void;
}

/**
 * Gives the physician the coverage decision, expected patient cost,
 * and useful alternatives. Detailed payer data remains available on
 * demand without competing with the prescribing decision.
 */
export function BenefitsCoordinationPanel({
  payerInformation,
  formulary,
  benefits,
  medication,
  hasCoverageResponse = false,
  isLoadingBenefits = false,
  priorAuthOutcome = null,
  expedited = false,
  onExpeditedChange,
}: BenefitsCoordinationPanelProps) {
  const [rtpbOpen, setRtpbOpen] = useState(false);
  const [insuranceDetailsOpen, setInsuranceDetailsOpen] = useState(false);

  const hasAnyData = Boolean(payerInformation || formulary || benefits);
  if (!hasAnyData) {
    if (!hasCoverageResponse && !isLoadingBenefits) {
      return null;
    }

    return (
      <div
        className={cn(
          'rounded-lg border border-border/60 bg-muted/30 p-4',
          'dark:border-border/40 dark:bg-muted/20'
        )}
      >
        {isLoadingBenefits ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Checking insurance coverage…
          </div>
        ) : (
          <EmptyCoverageState />
        )}
      </div>
    );
  }

  const display = deriveFormularyDisplayState({ formulary, medication });
  const restrictions = formulary?.Restrictions ?? null;
  const copays = formulary?.Copays ?? null;
  const hasRtpbDetailsPayload = hasRtpbDetails(benefits);
  const planName = payerInformation?.PlanName ?? payerInformation?.PayerName ?? null;
  const coverageSummary = describeFormularyCoverage(formulary?.FormularyStatus, restrictions);
  const primaryPricing = partitionCoveragesByPharmacyType(
    benefits?.PatientSpecificPricingCoverages ?? []
  ).primary?.PricingCoverage;
  const estimatedPatientPay = primaryPricing?.EstimatedPatientPayAmount;
  const publishedCopay = display.hideCopay ? null : selectPublishedCopay(copays);
  const isExcluded =
    priorAuthOutcome === PriorAuthGateOutcomeT.EXCLUDED ||
    restrictions?.IsExcluded === true ||
    display.bucket === 'excluded';
  const requiresPriorAuth =
    priorAuthOutcome === PriorAuthGateOutcomeT.PA_REQUIRED ||
    restrictions?.HasPriorAuthorization === true;
  const needsCoverageAttention =
    isExcluded || requiresPriorAuth || coverageSummary.tone !== 'positive';
  const statusTitle = isExcluded
    ? 'This medication may not be covered'
    : requiresPriorAuth
      ? 'Insurance approval required'
      : coverageSummary.title;
  const statusDescription = isExcluded
    ? 'The plan may exclude this medication. Review covered options before prescribing.'
    : requiresPriorAuth
      ? 'The plan requires prior authorization before the pharmacy can fill this prescription.'
      : coverageSummary.description;
  const costLabel =
    estimatedPatientPay != null
      ? `$${estimatedPatientPay}`
      : publishedCopay
        ? publishedCopay.description
        : null;

  return (
    <section className="rounded-[12px] border border-routine-outline/45 bg-surface-sunken p-4 shadow-sm dark:bg-secondary/35">
      <div
        className={cn(
          'inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium',
          needsCoverageAttention
            ? 'bg-amber-500/12 text-amber-700 dark:text-amber-200'
            : 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-200'
        )}
      >
        <span
          className={cn(
            'size-2 rounded-full',
            needsCoverageAttention ? 'bg-amber-500' : 'bg-emerald-500'
          )}
          aria-hidden
        />
        {statusTitle}
      </div>

      <p className="mt-3 max-w-md text-sm italic leading-5 text-muted-foreground">
        {statusDescription}
      </p>

      <details className="group mt-4 text-sm">
        <summary className="inline-flex cursor-pointer list-none items-center gap-2 rounded-full border border-border/80 bg-background/25 px-3 py-1.5 font-medium text-foreground transition-colors hover:bg-background/45">
          View insurance details
          <ChevronDown
            className="size-4 text-muted-foreground transition-transform group-open:rotate-180"
            aria-hidden
          />
        </summary>

        <div className="mt-4 space-y-5 border-l border-border/60 pl-4">
          {planName ? <SimpleDetailRow label="Plan" value={planName} /> : null}
          {costLabel ? (
            <SimpleDetailRow label="Estimated patient cost" value={costLabel} />
          ) : isLoadingBenefits ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
              Checking patient cost…
            </div>
          ) : (
            <SimpleDetailRow
              label="Estimated patient cost"
              value="Not provided by the patient’s plan"
            />
          )}

          {restrictions?.HasStepTherapy ? (
            <SimpleDetailRow
              label="Plan requirement"
              value="The patient may need to try another medication first."
            />
          ) : null}

          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              radius="pill"
              className="border-foreground/20 bg-transparent text-foreground shadow-none hover:bg-secondary/40 hover:text-foreground dark:bg-transparent dark:hover:bg-secondary/40"
              onClick={() => setInsuranceDetailsOpen(true)}
            >
              Full plan details
            </Button>
          </div>

          {requiresPriorAuth && onExpeditedChange ? (
            <label className="flex max-w-sm cursor-pointer items-start gap-3">
              <Checkbox
                id="pa-expedited"
                className="mt-0.5"
                checked={expedited}
                onCheckedChange={(checked) => onExpeditedChange(checked === true)}
              />
              <span>
                <span className="block text-sm font-medium">Request expedited review</span>
                <span className="mt-0.5 block text-xs leading-4 text-muted-foreground">
                  For urgent cases when waiting could put the patient’s health or recovery at risk.
                </span>
              </span>
            </label>
          ) : null}
        </div>
      </details>

      <FormularyDetailDialog
        open={insuranceDetailsOpen}
        onOpenChange={setInsuranceDetailsOpen}
        formularyStatus={formulary?.FormularyStatus}
        restrictions={restrictions}
        copays={copays}
        payerInformation={payerInformation}
        onViewCostDetails={
          hasRtpbDetailsPayload
            ? () => {
                setInsuranceDetailsOpen(false);
                setRtpbOpen(true);
              }
            : undefined
        }
      />

      {hasRtpbDetailsPayload ? (
        <RtpbDialog open={rtpbOpen} onOpenChange={setRtpbOpen} benefits={benefits!} />
      ) : null}
    </section>
  );
}

function SimpleDetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-medium leading-5">{value}</div>
    </div>
  );
}

function EmptyCoverageState() {
  return (
    <div className="flex items-start gap-3">
      <ShieldCheck className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div>
        <div className="text-sm font-medium text-foreground/90">
          Coverage could not be confirmed
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          No insurance coverage information was returned for this prescription.
        </div>
      </div>
    </div>
  );
}

function selectPublishedCopay(copays: PayerCopayT[] | null | undefined): {
  description: string;
  pharmacyLabel: string;
} | null {
  if (!copays) {
    return null;
  }
  const copay =
    copays.find((item) => item.PharmacyType === 'Retail') ??
    copays.find((item) => item.PharmacyType === 'Any') ??
    copays[0];
  if (!copay) {
    return null;
  }
  const description =
    formatDollar(copay.FlatCopayAmount) ??
    formatPercent(copay.PercentCopayAmount) ??
    describeCopay(copay);
  if (!description) {
    return null;
  }
  return { description, pharmacyLabel: pharmacyTypeLabel(copay.PharmacyType ?? null) };
}

export function PlanAlternativesDialog({
  open,
  onOpenChange,
  display,
  payerInformation,
  isLoading = false,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  display: FormularyDisplayState;
  payerInformation: PayerInformationT | null | undefined;
  isLoading?: boolean;
  onSelectAlternative?: AlternativeSelectionHandler;
  isSelectingAlternative: boolean;
}) {
  const planLabel = payerInformation?.PlanName ?? payerInformation?.PayerName ?? null;
  const hasAnyAlternatives =
    display.payerAlternatives.length > 0 || display.therapeuticAlternatives.length > 0;

  const handleSelect = onSelectAlternative
    ? async (dispensableDrugId: number) => {
        const didSelect = await onSelectAlternative(dispensableDrugId);
        if (didSelect !== false) {
          onOpenChange(false);
        }
      }
    : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[min(80vh,44rem)] min-w-0 grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader className="min-w-0 px-6 pb-5 pr-14 pt-6">
          <DialogTitle>Plan alternatives</DialogTitle>
          <DialogDescription>
            {handleSelect
              ? 'These options may cost less or avoid insurance approval. Confirm clinical appropriateness before changing the prescription.'
              : 'These options come from the patient’s plan. This prescription will not change from this screen.'}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 min-w-0 space-y-5 overflow-x-hidden overflow-y-auto overscroll-contain border-t border-border/60 px-6 py-5">
          {planLabel ? (
            <div className="text-xs text-muted-foreground">Patient plan: {planLabel}</div>
          ) : null}

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Loading plan alternatives…
            </div>
          ) : null}

          {!isLoading && display.policy.notice ? (
            <div className="border-l border-border/70 pl-3 text-xs leading-4 text-muted-foreground">
              {display.policy.notice}
            </div>
          ) : null}

          {!isLoading && display.payerAlternatives.length > 0 ? (
            <PayerAlternativesSection
              alternatives={display.payerAlternatives}
              onSelectAlternative={handleSelect}
              isSelectingAlternative={isSelectingAlternative}
            />
          ) : null}

          {!isLoading && display.therapeuticAlternatives.length > 0 ? (
            <TherapeuticAlternativesSection
              alternatives={display.therapeuticAlternatives}
              onSelectAlternative={handleSelect}
              isSelectingAlternative={isSelectingAlternative}
            />
          ) : null}

          {!isLoading && !hasAnyAlternatives && !display.policy.notice ? (
            <div className="py-4 text-center text-sm text-muted-foreground">
              No plan alternatives are available for this medication.
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RtpbDialog({
  open,
  onOpenChange,
  benefits,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  benefits: PrescriptionBenefitsResponseT;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="size-4" aria-hidden />
            Patient cost details
          </DialogTitle>
          <DialogDescription>
            Estimated pricing for this prescription. The pharmacy determines the final price.
          </DialogDescription>
        </DialogHeader>
        <RtpbSection benefits={benefits} isSelectingAlternative={false} />
      </DialogContent>
    </Dialog>
  );
}

function describeRealtimeStatus(status: number): string {
  switch (status) {
    case 0:
      return 'Queued';
    case 1:
      return 'Sent';
    case 2:
      return 'Success';
    case 3:
      return 'No realtime lookup';
    default:
      return 'Error or unavailable';
  }
}

/* ------------------------------------------------------------------ */
/*  RTPB — Real-Time Prescription Benefits (PM.105–PM.112)            */
/* ------------------------------------------------------------------ */

function hasRtpbData(benefits: PrescriptionBenefitsResponseT | null | undefined): boolean {
  const coverages = benefits?.PatientSpecificPricingCoverages;
  return (coverages?.length ?? 0) > 0;
}

function hasRtpbDetails(benefits: PrescriptionBenefitsResponseT | null | undefined): boolean {
  if (!benefits) {
    return false;
  }
  return (
    hasRtpbData(benefits) ||
    normalizeRtpbAlternatives(benefits.Alternatives ?? []).length > 0 ||
    benefits.OptionalPharmacy != null
  );
}

function RtpbEmptyState({ realtimeStatus }: { realtimeStatus: number | null }) {
  return (
    <div className="rounded-md border border-border/40 bg-background/40 px-3 py-2 text-xs dark:border-border/30 dark:bg-background/20">
      <div className="font-medium text-foreground/90">No patient cost estimate returned</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">
        The insurer did not return a patient-specific price for this prescription.
        {realtimeStatus != null
          ? ` Realtime status: ${describeRealtimeStatus(realtimeStatus)}.`
          : ''}
      </div>
    </div>
  );
}

function isDrugDiscountResponse(coverages: PatientSpecificPricingCoverageT[]): boolean {
  return coverages.some((c) => {
    const status = c.PricingCoverage?.DrugStatus ?? '';
    return status.toLowerCase().includes('drug discount price');
  });
}

function RtpbSection({
  benefits,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  benefits: PrescriptionBenefitsResponseT;
  onSelectAlternative?: AlternativeSelectionHandler;
  isSelectingAlternative: boolean;
}) {
  const coverages = benefits.PatientSpecificPricingCoverages ?? [];
  const alternatives = benefits.Alternatives ?? [];
  const [showOtherPharmacies, setShowOtherPharmacies] = useState(false);

  const { primary, others } = partitionCoveragesByPharmacyType(coverages);
  const isDrugDiscount = isDrugDiscountResponse(coverages);

  return (
    <div className="space-y-3">
      {isDrugDiscount ? (
        <div className="rounded-md border border-violet-400/40 bg-violet-50/60 px-2.5 py-1.5 text-[11px] font-medium text-violet-800 dark:border-violet-500/30 dark:bg-violet-950/30 dark:text-violet-200">
          This is a discount price, not an insurance benefit.
        </div>
      ) : null}

      {benefits.OptionalPharmacy && !primary?.Pharmacy ? (
        <PharmacyDetailBlock pharmacy={benefits.OptionalPharmacy} />
      ) : null}

      {coverages.length === 0 ? (
        <RtpbEmptyState realtimeStatus={benefits.RealTimeFormularyRequestStatus ?? null} />
      ) : null}

      {primary ? <PricingCoverageCard coverage={primary} label="Selected pharmacy" /> : null}

      {others.length > 0 ? (
        <div>
          <button
            type="button"
            className="flex w-full items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
            onClick={() => setShowOtherPharmacies((v) => !v)}
          >
            <Store className="size-3" aria-hidden />
            Pricing at {others.length} other {others.length === 1 ? 'pharmacy' : 'pharmacies'}
            <ChevronDown
              className={cn(
                'ml-auto size-3.5 transition-transform',
                showOtherPharmacies && 'rotate-180'
              )}
              aria-hidden
            />
          </button>
          {showOtherPharmacies ? (
            <div className="mt-2 space-y-2">
              {others.map((cov, idx) => (
                <PricingCoverageCard
                  key={`other-pharm-${idx}`}
                  coverage={cov}
                  label={pharmacyLabel(cov)}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {alternatives.length > 0 ? (
        <RtpbAlternativesSection
          alternatives={alternatives}
          onSelectAlternative={onSelectAlternative}
          isSelectingAlternative={isSelectingAlternative}
        />
      ) : null}
    </div>
  );
}

function partitionCoveragesByPharmacyType(coverages: PatientSpecificPricingCoverageT[]): {
  primary: PatientSpecificPricingCoverageT | null;
  others: PatientSpecificPricingCoverageT[];
} {
  const retailIdx = coverages.findIndex(
    (c) => c.Pharmacy?.PharmacyType?.toLowerCase() === 'retail'
  );
  const primaryIdx = retailIdx >= 0 ? retailIdx : 0;
  const primary = coverages[primaryIdx] ?? null;
  const others = coverages.filter((_, idx) => idx !== primaryIdx);
  return { primary, others };
}

function pharmacyLabel(coverage: PatientSpecificPricingCoverageT): string {
  const type = coverage.Pharmacy?.PharmacyType;
  const name = coverage.Pharmacy?.StoreName;
  if (type && name) {
    return `${type} — ${name}`;
  }
  return type ?? name ?? 'Pharmacy';
}

function formatPharmacyAddress(pharmacy: PrescriptionBenefitsPharmacyT): string | null {
  const parts = [pharmacy.Address1, pharmacy.Address2].filter(Boolean);
  const cityState = [pharmacy.City, pharmacy.State].filter(Boolean).join(', ');
  const cityStateZip = [cityState, pharmacy.ZipCode].filter(Boolean).join(' ');
  if (cityStateZip) {
    parts.push(cityStateZip);
  }
  return parts.length > 0 ? parts.join(', ') : null;
}

function formatPharmacistName(pharmacy: PrescriptionBenefitsPharmacyT): string | null {
  const parts = [
    pharmacy.PharmacistPrefix,
    pharmacy.PharmacistFirstName,
    pharmacy.PharmacistMiddleName,
    pharmacy.PharmacistLastName,
    pharmacy.PharmacistSuffix,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

/**
 * Pull the pharmacy NPI out of the loosely-typed ``Identifications``
 * bag. DoseSpot's RTPB swagger types this as an open object so payers
 * can attach arbitrary id types (NPI, NABP, federal tax id, etc.); we
 * accept any key that case-insensitively matches an NPI-style label.
 */
function extractNpi(pharmacy: PrescriptionBenefitsPharmacyT): string | null {
  const ids = pharmacy.Identifications;
  if (!ids || typeof ids !== 'object') {
    return null;
  }
  for (const [key, value] of Object.entries(ids)) {
    const normalized = key.toLowerCase().replace(/[^a-z]/g, '');
    if (normalized === 'npi' || normalized === 'npinumber' || normalized === 'npiid') {
      if (value == null) {
        return null;
      }
      const str = String(value).trim();
      return str === '' ? null : str;
    }
  }
  return null;
}

/** ``Identifications`` keys that have first-class rows elsewhere in the
 * detail block; skip them when listing "other" identifications so we
 * don't render duplicates. */
const NPI_IDENTIFICATION_KEYS = new Set(['npi', 'npinumber', 'npiid']);

function listOtherIdentifications(
  pharmacy: PrescriptionBenefitsPharmacyT
): { label: string; value: string }[] {
  const ids = pharmacy.Identifications;
  if (!ids || typeof ids !== 'object') {
    return [];
  }
  const out: { label: string; value: string }[] = [];
  for (const [key, value] of Object.entries(ids)) {
    const normalized = key.toLowerCase().replace(/[^a-z]/g, '');
    if (NPI_IDENTIFICATION_KEYS.has(normalized)) {
      continue;
    }
    if (value == null) {
      continue;
    }
    const str = String(value).trim();
    if (str === '') {
      continue;
    }
    out.push({ label: key, value: str });
  }
  return out;
}

function DetailRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | number | null | undefined;
  mono?: boolean;
}) {
  if (value == null || value === '') {
    return null;
  }
  return (
    <>
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? 'font-mono' : undefined}>{value}</span>
    </>
  );
}

function PharmacyDetailBlock({ pharmacy }: { pharmacy: PrescriptionBenefitsPharmacyT }) {
  const [showMore, setShowMore] = useState(false);

  const address = formatPharmacyAddress(pharmacy);
  const pharmacistName = formatPharmacistName(pharmacy);
  const npi = extractNpi(pharmacy);
  const otherIdentifications = listOtherIdentifications(pharmacy);

  // Essential-row visibility. The block hides itself only when nothing
  // identifying is published — falling back through Identifications-based
  // ids covers RTPB rows where DoseSpot omits the structured pharmacy
  // slice entirely.
  const hasEssential =
    pharmacy.StoreName ||
    address ||
    pharmacy.PrimaryPhone ||
    pharmacy.PharmacyType ||
    npi ||
    pharmacy.NCPDPID;

  const additionalPhones: { label: string; value: string }[] = [];
  if (pharmacy.PhoneAdditional1) {
    additionalPhones.push({
      label: pharmacy.PhoneAdditionalType1 ?? 'Phone (alt 1)',
      value: pharmacy.PhoneAdditional1,
    });
  }
  if (pharmacy.PhoneAdditional2) {
    additionalPhones.push({
      label: pharmacy.PhoneAdditionalType2 ?? 'Phone (alt 2)',
      value: pharmacy.PhoneAdditional2,
    });
  }
  if (pharmacy.PhoneAdditional3) {
    additionalPhones.push({
      label: pharmacy.PhoneAdditionalType3 ?? 'Phone (alt 3)',
      value: pharmacy.PhoneAdditional3,
    });
  }

  const hasMore =
    pharmacy.PrimaryFax != null ||
    additionalPhones.length > 0 ||
    pharmacistName != null ||
    pharmacy.Email != null ||
    pharmacy.PharmacyId != null ||
    pharmacy.NCPDPID != null ||
    pharmacy.ServiceLevel != null ||
    (pharmacy.PharmacySpecialties?.length ?? 0) > 0 ||
    pharmacy.DistanceFromRequestedPharmacy != null ||
    pharmacy.Latitude != null ||
    pharmacy.Longitude != null ||
    otherIdentifications.length > 0;

  if (!hasEssential && !hasMore) {
    return null;
  }

  const coords =
    pharmacy.Latitude != null && pharmacy.Longitude != null
      ? `${pharmacy.Latitude}, ${pharmacy.Longitude}`
      : null;
  const distance =
    pharmacy.DistanceFromRequestedPharmacy != null
      ? `${pharmacy.DistanceFromRequestedPharmacy} mi`
      : null;
  const specialties = pharmacy.PharmacySpecialties?.join(', ') || null;

  return (
    <div className="mb-2 rounded border border-border/30 bg-muted/20 px-2.5 py-1.5 text-[11px]">
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
        {pharmacy.StoreName ? (
          <>
            <span className="text-muted-foreground">Store</span>
            <span className="font-medium">{pharmacy.StoreName}</span>
          </>
        ) : null}
        <DetailRow label="Type" value={pharmacy.PharmacyType ?? null} />
        <DetailRow label="Address" value={address} />
        <DetailRow label="Phone" value={pharmacy.PrimaryPhone ?? null} mono />
        <DetailRow label="NPI" value={npi} mono />
      </div>

      {showMore ? (
        <div className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 border-t border-border/30 pt-1">
          <DetailRow label="Fax" value={pharmacy.PrimaryFax ?? null} mono />
          {additionalPhones.map((entry, idx) => (
            <DetailRow key={`addl-phone-${idx}`} label={entry.label} value={entry.value} mono />
          ))}
          <DetailRow label="Pharmacist" value={pharmacistName} />
          <DetailRow label="Email" value={pharmacy.Email ?? null} />
          <DetailRow label="Pharmacy ID" value={pharmacy.PharmacyId ?? null} mono />
          <DetailRow label="NCPDP ID" value={pharmacy.NCPDPID ?? null} mono />
          <DetailRow label="Service Level" value={pharmacy.ServiceLevel ?? null} />
          <DetailRow label="Specialties" value={specialties} />
          <DetailRow label="Distance" value={distance} />
          <DetailRow label="Coordinates" value={coords} mono />
          {otherIdentifications.map((entry) => (
            <DetailRow key={`id-${entry.label}`} label={entry.label} value={entry.value} mono />
          ))}
        </div>
      ) : null}

      {hasMore ? (
        <button
          type="button"
          className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
          onClick={() => setShowMore((v) => !v)}
          aria-expanded={showMore}
        >
          {showMore ? 'Show less' : 'Show more'}
          <ChevronDown
            className={cn('size-3 transition-transform', showMore && 'rotate-180')}
            aria-hidden
          />
        </button>
      ) : null}
    </div>
  );
}

function PricingCoverageCard({
  coverage,
  label,
  defaultExpanded = false,
}: {
  coverage: PatientSpecificPricingCoverageT;
  label: string;
  defaultExpanded?: boolean;
}) {
  const [showFactors, setShowFactors] = useState(defaultExpanded);
  const pricing = coverage.PricingCoverage;
  if (!pricing) {
    return null;
  }

  const pharmacy = coverage.Pharmacy;
  const alerts = pricing.CoverageAlerts ?? [];
  const hasAlerts = alerts.length > 0;

  return (
    <div className="rounded-xl border border-border/50 bg-background/40 p-4 dark:border-border/30 dark:bg-background/20">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="mt-1 text-2xl font-semibold tracking-tight">
            {pricing.EstimatedPatientPayAmount != null
              ? `$${pricing.EstimatedPatientPayAmount}`
              : 'Price unavailable'}
          </div>
        </div>
        <div className="pb-0.5 text-right text-sm text-muted-foreground">
          {formatQuantityPriced(pricing) ? <div>{formatQuantityPriced(pricing)}</div> : null}
          {pricing.DaysSupplyPriced != null ? (
            <div>{pricing.DaysSupplyPriced}-day supply</div>
          ) : null}
        </div>
      </div>

      <div className="mt-2">
        <button
          type="button"
          className="flex w-full items-center gap-1 text-[10px] font-medium text-muted-foreground hover:text-foreground"
          onClick={() => setShowFactors((v) => !v)}
        >
          More details
          {pricing.PriorAuthRequired ? (
            <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              PA
            </span>
          ) : null}
          {hasAlerts ? (
            <span className="ml-1 rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-semibold text-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
              Alerts
            </span>
          ) : null}
          <ChevronDown
            className={cn('ml-auto size-3 transition-transform', showFactors && 'rotate-180')}
            aria-hidden
          />
        </button>
        {showFactors ? (
          <div className="space-y-2">
            {pharmacy ? <PharmacyDetailBlock pharmacy={pharmacy} /> : null}
            <CoverageFactorsDetail pricing={pricing} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function CoverageFactorsDetail({ pricing }: { pricing: PricingCoverageFactorsT }) {
  const alertList = pricing.CoverageAlerts ?? [];

  return (
    <div className="mt-1.5 space-y-1.5 border-t border-border/30 pt-1.5">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 @md:grid-cols-3">
        <PricingField label="Drug Status" value={pricing.DrugStatus ?? pricing.DrugStatusCode} />
        <PricingField label="Formulary Status" value={pricing.FormularyStatus} />
        <PricingField
          label="Prior Auth"
          value={
            pricing.PriorAuthRequired
              ? 'Required'
              : pricing.PriorAuthRequired === false
                ? 'No'
                : null
          }
        />
        <PricingField label="Plan Pay" value={pricing.PlanPayAmount} prefix="$" />
        <PricingField label="OOP Applied" value={pricing.OOPAppliedAmount} prefix="$" />
        <PricingField label="OOP Remaining" value={pricing.OOPRemainingAmount} prefix="$" />
        <PricingField
          label="Deductible Applied"
          value={pricing.DeductibleAppliedAmount}
          prefix="$"
        />
        <PricingField
          label="Deductible Remaining"
          value={pricing.DeductibleRemainingAmount}
          prefix="$"
        />
      </div>
      {alertList.length > 0 ? (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Coverage Alerts
          </div>
          <ul className="mt-0.5 space-y-0.5">
            {alertList.map((alert, idx) => (
              <li
                key={`alert-${idx}`}
                className="rounded border border-border/30 bg-muted/20 px-2 py-0.5 text-xs"
              >
                {alert.ReferenceCode ? (
                  <span className="font-medium">{alert.ReferenceCode} </span>
                ) : null}
                <LinkifyText text={alert.ReferenceText ?? ''} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

interface RtpbAlternativeRowModel {
  key: string;
  displayName: string;
  ndc: string | null;
  dispensableDrugId: number | null;
  coverages: PatientSpecificPricingCoverageT[];
  coverage: PrescriptionBenefitPayerAlternativeDetailT['Coverage'] | null | undefined;
}

function normalizeRtpbAlternatives(alternatives: PayerAlternativeT[]): RtpbAlternativeRowModel[] {
  return alternatives.flatMap((alt, altIdx) => {
    const details = alt.Details ?? [];
    if (details.length > 0) {
      return details.map((detail, detailIdx) => ({
        key: detail.Ndc ?? alt.Ndc ?? `rtpb-alt-${altIdx}-detail-${detailIdx}`,
        displayName: detail.DisplayName ?? alt.DisplayName ?? detail.Ndc ?? alt.Ndc ?? '—',
        ndc: detail.Ndc ?? alt.Ndc ?? null,
        dispensableDrugId: detail.DispensableDrugId ?? alt.DispensableDrugId ?? null,
        coverages:
          detail.PatientSpecificPricingCoverages ?? alt.PatientSpecificPricingCoverages ?? [],
        coverage: detail.Coverage ?? null,
      }));
    }

    return [
      {
        key: alt.Ndc ?? `rtpb-alt-${altIdx}`,
        displayName: alt.DisplayName ?? alt.Ndc ?? '—',
        ndc: alt.Ndc ?? null,
        dispensableDrugId: alt.DispensableDrugId ?? null,
        coverages: alt.PatientSpecificPricingCoverages ?? [],
        coverage: null,
      },
    ];
  });
}

function RtpbAlternativesSection({
  alternatives,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  alternatives: PayerAlternativeT[];
  onSelectAlternative?: AlternativeSelectionHandler;
  isSelectingAlternative: boolean;
}) {
  const rows = normalizeRtpbAlternatives(alternatives);
  if (rows.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        <Pill className="size-3" aria-hidden />
        RTPB Alternatives
      </div>
      <div className="space-y-2">
        {rows.map((alt) => (
          <RtpbAlternativeCard
            key={alt.key}
            alt={alt}
            onSelectAlternative={onSelectAlternative}
            isSelectingAlternative={isSelectingAlternative}
          />
        ))}
      </div>
    </div>
  );
}

function RtpbAlternativeCard({
  alt,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  alt: RtpbAlternativeRowModel;
  onSelectAlternative?: AlternativeSelectionHandler;
  isSelectingAlternative: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const coverages = alt.coverages;
  const { primary, others } = partitionCoveragesByPharmacyType(coverages);
  const isClickable =
    onSelectAlternative !== undefined && !!alt.dispensableDrugId && !isSelectingAlternative;

  return (
    <div className="rounded-md border border-border/40 bg-background/40 dark:border-border/30 dark:bg-background/20">
      <div className="px-2.5 py-2">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="truncate text-xs font-medium">{alt.displayName}</div>
            {alt.ndc ? (
              <span className="font-mono text-[10px] text-muted-foreground">NDC {alt.ndc}</span>
            ) : null}
          </div>
          {isClickable && onSelectAlternative ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              radius="pill"
              onClick={() => onSelectAlternative(alt.dispensableDrugId!)}
              aria-label={`Switch to ${alt.displayName}`}
            >
              <span className="text-[11px]">Use this</span>
              <ArrowRight className="ml-1 size-3" aria-hidden />
            </Button>
          ) : null}
        </div>

        {alt.coverage?.FormularyStatus ? (
          <div className="mt-1 text-[10px] text-muted-foreground">
            Coverage status:{' '}
            {formularyStatusLabel(alt.coverage.FormularyStatus) ?? alt.coverage.FormularyStatus}
          </div>
        ) : null}

        {primary?.Pharmacy ? <PharmacyDetailBlock pharmacy={primary.Pharmacy} /> : null}

        {primary?.PricingCoverage ? (
          <div className="mt-1.5 grid grid-cols-3 gap-x-4 gap-y-1">
            <PricingField
              label="Est. Patient Pay"
              value={primary.PricingCoverage.EstimatedPatientPayAmount}
              prefix="$"
            />
            <PricingField
              label="Qty Priced"
              value={formatQuantityPriced(primary.PricingCoverage)}
            />
            <PricingField label="Days Supply" value={primary.PricingCoverage.DaysSupplyPriced} />
          </div>
        ) : null}

        {others.length > 0 || primary?.PricingCoverage ? (
          <button
            type="button"
            className="mt-1.5 flex w-full items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded((v) => !v)}
          >
            {primary?.PricingCoverage?.PriorAuthRequired ? (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                PA
              </span>
            ) : null}
            {primary?.PricingCoverage?.FormularyStatus != null ? (
              <span className="text-[10px]">Status {primary.PricingCoverage.FormularyStatus}</span>
            ) : null}
            {others.length > 0 ? (
              <span className="text-[10px]">
                + {others.length} other {others.length === 1 ? 'pharmacy' : 'pharmacies'}
              </span>
            ) : null}
            {coverages.some((c) => c.PricingCoverage?.CoverageAlerts?.length) ? (
              <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-semibold text-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
                Alerts
              </span>
            ) : null}
            <span className="ml-auto text-[10px]">Details</span>
            <ChevronDown
              className={cn('size-3 transition-transform', expanded && 'rotate-180')}
              aria-hidden
            />
          </button>
        ) : null}
      </div>

      {expanded ? (
        <div className="space-y-2 border-t border-border/30 px-2.5 py-2">
          {primary?.PricingCoverage ? (
            <CoverageFactorsDetail pricing={primary.PricingCoverage} />
          ) : null}
          {others.map((cov, idx) => (
            <PricingCoverageCard
              key={`alt-other-${idx}`}
              coverage={cov}
              label={pharmacyLabel(cov)}
              defaultExpanded={false}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function formatQuantityPriced(pricing: PricingCoverageFactorsT): string | null {
  const value = pricing.QuantityPricedValue;
  if (value == null) {
    return null;
  }
  const uom = pricing.QuantityPricedUnitOfMeasureType;
  return uom ? `${value} ${uom}` : String(value);
}

function PricingField({
  label,
  value,
  prefix,
}: {
  label: string;
  value: string | number | boolean | null | undefined;
  prefix?: string;
}) {
  if (value == null || value === '') {
    return null;
  }
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-xs font-medium">
        {prefix ?? ''}
        {String(value)}
      </div>
    </div>
  );
}

export function PayerAlternativesSection({
  alternatives,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  alternatives: PayerAlternativeT[];
  onSelectAlternative?: AlternativeSelectionHandler;
  isSelectingAlternative: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="mb-1.5 text-xs font-medium text-muted-foreground">Preferred by plan</div>
      <div className="min-w-0 divide-y divide-border/60 border-y border-border/60">
        {alternatives.map((alt, idx) => {
          const dispensableDrugId = alt.DispensableDrugId ?? null;
          const rawCode = alt.RepresentativeFormularyCoverage?.FormularyStatus ?? null;
          const bucket = bucketFormularyStatus(rawCode, null);
          const showCopay = bucket !== 'unknown' && bucket !== 'not-reimbursable';
          const copay = showCopay
            ? pickAlternativeRetailCopay(alt.RepresentativeFormularyCoverage?.CopayFactors)
            : null;
          const isClickable =
            onSelectAlternative !== undefined &&
            dispensableDrugId !== null &&
            !isSelectingAlternative;
          return (
            <AlternativeRow
              key={alt.Ndc ?? `payer-alt-${idx}`}
              displayName={alt.DisplayName ?? alt.Ndc ?? '—'}
              brand={alt.Brand}
              copay={copay}
              isClickable={isClickable}
              onSelect={
                onSelectAlternative && dispensableDrugId !== null
                  ? () => onSelectAlternative(dispensableDrugId)
                  : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}

export function TherapeuticAlternativesSection({
  alternatives,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  alternatives: TherapeuticAlternativesT[];
  onSelectAlternative?: AlternativeSelectionHandler;
  isSelectingAlternative: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Pill className="size-3" aria-hidden />
        Other options from plan
      </div>
      <div className="min-w-0 divide-y divide-border/60 border-y border-border/60">
        {alternatives.map((alt, idx) => {
          const dispensableDrugId = alt.DispensableDrugId ?? null;
          const rawCode = alt.FormularyStatus ?? null;
          const bucket = bucketFormularyStatus(rawCode, null);
          const showCopay = bucket !== 'unknown' && bucket !== 'not-reimbursable';
          const copay = showCopay ? (alt.Copay ?? null) : null;
          const isClickable =
            onSelectAlternative !== undefined &&
            dispensableDrugId !== null &&
            !isSelectingAlternative;
          return (
            <AlternativeRow
              key={alt.Ndc ?? `therapeutic-alt-${idx}`}
              displayName={alt.FullDisplayString ?? alt.DisplayName ?? alt.Ndc ?? '—'}
              brand={alt.FormularyInfo?.IsGeneric != null ? !alt.FormularyInfo.IsGeneric : null}
              copay={copay}
              isClickable={isClickable}
              onSelect={
                onSelectAlternative && dispensableDrugId !== null
                  ? () => onSelectAlternative(dispensableDrugId)
                  : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}

function AlternativeRow({
  displayName,
  brand,
  copay,
  isClickable,
  onSelect,
}: {
  displayName: string;
  brand: boolean | null | undefined;
  copay: PayerCopayT | null;
  isClickable: boolean;
  onSelect: (() => void) | undefined;
}) {
  const copayDescription = copay ? describeCopay(copay) : null;
  return (
    <div
      className={cn(
        'grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-3.5',
        isClickable && 'pr-1'
      )}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="min-w-0 break-words text-sm font-medium leading-5 [overflow-wrap:anywhere]">
          {displayName}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {brand != null ? <span>{brand ? 'Brand' : 'Generic'}</span> : null}
          {copayDescription ? <span>· {copayDescription}</span> : null}
        </div>
      </div>
      {isClickable && onSelect ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          radius="pill"
          onClick={onSelect}
          aria-label={`Switch to ${displayName}`}
          className="shrink-0"
        >
          <span className="text-xs">Switch</span>
          <ArrowRight className="ml-1 size-3" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  PM.122.1: Linkify URLs in coverage alert text                     */
/* ------------------------------------------------------------------ */

const URL_PATTERN = /(https?:\/\/[^\s)]+)/i;

function LinkifyText({ text }: { text: string }) {
  if (!text) {
    return null;
  }
  const parts = text.split(URL_PATTERN);
  if (parts.length === 1) {
    return <>{text}</>;
  }

  return (
    <>
      {parts.map((part, idx) =>
        URL_PATTERN.test(part) ? (
          <a
            key={idx}
            href={part}
            target="_blank"
            rel="noreferrer noopener"
            className="text-sky-700 underline underline-offset-2 hover:text-sky-900 dark:text-sky-300 dark:hover:text-sky-100"
          >
            {part}
          </a>
        ) : (
          <span key={idx}>{part}</span>
        )
      )}
    </>
  );
}
